// Botón de compartir de un producto. En el teléfono abre la hoja nativa del
// sistema con la foto ya adjunta, así que el destino lo elige quien comparte:
// WhatsApp, Instagram, Telegram, copiar… Por eso el botón dice "Compartir" y no
// "Compartir por WhatsApp": prometer un destino concreto era mentir sobre lo
// que hace. En escritorio, cuando el navegador no tiene hoja de compartir, cae
// a wa.me, que es donde acaban los pedidos de este negocio.
//
// Detalle que no se puede tocar: WhatsApp IGNORA el campo `url` de
// navigator.share() cuando el payload lleva archivos. Por eso el link va
// escrito dentro del texto y nunca se pasa como `url`.

import React, { useState } from 'react';
import { toast } from 'sonner';
import { buildShareMessage } from '../lib/productShare';
import { buildProductFlyer } from '../lib/productFlyer';

// El botón es secundario: la acción del negocio es "Agregar al Pedido", y un
// botón de ancho completo con color propio le robaba protagonismo.
const MUTED = '#6f5348'; // 6.5:1 sobre blanco: discreto, pero legible al sol

const ShareIcon = ({ color }) => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill={color} aria-hidden="true">
    <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z" />
  </svg>
);

const ShareProductButton = ({ product, type, slug, variant = 'inline' }) => {
  const [sharing, setSharing] = useState(false);

  if (!product || !slug) return null;

  const openWhatsAppWeb = (message) => {
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank', 'noopener,noreferrer');
  };

  const handleShare = async () => {
    if (sharing) return;
    setSharing(true);

    const message = buildShareMessage(product, type, slug);

    try {
      // 1. Móvil con Web Share de archivos: foto adjunta + texto de pie.
      if (navigator.share && navigator.canShare) {
        const blob = await buildProductFlyer(product, type);
        if (blob) {
          const file = new File([blob], `${slug}.jpg`, { type: 'image/jpeg' });
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({ files: [file], text: message });
            return;
          }
        }
      }

      // 2. Web Share sin archivos: solo el texto (que ya lleva el link dentro).
      if (navigator.share) {
        await navigator.share({ title: product.name, text: message });
        return;
      }

      // 3. Escritorio: wa.me. La tarjeta de preview del link aporta la foto.
      openWhatsAppWeb(message);
    } catch (error) {
      // Cancelar la hoja de compartir no es un fallo.
      if (error?.name === 'AbortError') return;

      // Si share() falló por cualquier otra razón, todavía queda wa.me: es el
      // único camino que funciona en todas partes, y antes era inalcanzable en
      // cuanto el navegador tenía navigator.share.
      try {
        openWhatsAppWeb(message);
        return;
      } catch {
        /* bloqueador de popups u otro impedimento: al portapapeles */
      }
      try {
        await navigator.clipboard.writeText(message);
        toast.success('Copiado — pégalo donde quieras');
      } catch {
        toast.error('No se pudo compartir desde este dispositivo');
      }
    } finally {
      setSharing(false);
    }
  };

  const isIcon = variant === 'icon';

  return (
    <button
      type="button"
      onClick={handleShare}
      disabled={sharing}
      aria-label={`Compartir ${product.name}`}
      title="Compartir"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: isIcon ? 0 : '7px',
        width: isIcon ? '44px' : '100%',
        // 44px de alto aunque no se vea el recuadro: es el área que se toca.
        minHeight: '44px',
        padding: isIcon ? 0 : '6px 12px',
        borderRadius: isIcon ? '50%' : '12px',
        border: 'none',
        background: 'transparent',
        color: MUTED,
        fontSize: '14px',
        fontWeight: 500,
        cursor: sharing ? 'wait' : 'pointer',
        opacity: sharing ? 0.6 : 1,
        transition: 'opacity 0.2s',
      }}
    >
      <ShareIcon color={MUTED} />
      {!isIcon && <span>{sharing ? 'Preparando…' : 'Compartir'}</span>}
    </button>
  );
};

export default ShareProductButton;
